/**
 * Фильтр в адресе страницы (PROP-391).
 *
 * ЗАЧЕМ. Агент умеет давать ссылки внутрь платформы (PROP-386), и «покажи не удалённые» должно
 * заканчиваться ссылкой на УЖЕ ОТФИЛЬТРОВАННУЮ таблицу, а не пересказом того, какие кнопки
 * нажать. Синтетические клики для этого не нужны и вредны: фильтр — это структура данных
 * (`userFilter` таблицы), а клики зависят от вёрстки и ломаются от любой её правки.
 *
 * Побочная выгода, ради которой стоит держать состояние именно в адресе: оно переживает
 * перезагрузку, пересылается коллеге и возвращается кнопкой «назад».
 *
 * ⚠️ ЗНАЧЕНИЕ ПРИХОДИТ СНАРУЖИ. Ссылку пишет модель, правит человек, пересылает третий — то
 * есть это недоверенный ввод в чистом виде. Отсюда два правила: разбор НИКОГДА не бросает
 * наружу (страница обязана открыться даже с мусором в адресе), и структура проверяется по
 * форме, а не принимается на веру.
 */

/** Базовый узел фильтра — тот же, с каким таблица живёт без фильтра. */
export function emptyFilterNode() {
  return {
    node: {
      isactive: false,
      isexpanded: false,
      name: 'Root Node',
      oper: { id: 1, sid: 'and', name: 'AND' },
      nodes: [],
      conds: [],
    },
  }
}

/**
 * Проверка формы. Не «валидация ради валидации»: сюда приходит чужой JSON, и таблица, получив
 * узел без `conds`, падает уже в глубине отрисовки — там, где причина не видна.
 */
export function isFilterShape(value) {
  if (!value || typeof value !== 'object') return false
  const { node } = value
  if (!node || typeof node !== 'object') return false
  if (!Array.isArray(node.conds) || !Array.isArray(node.nodes)) return false
  // Операция обязательна: без неё непонятно, «и» или «или» связывает условия, а угадывать за
  // человека, какие строки он хотел увидеть, нельзя.
  if (!node.oper || typeof node.oper !== 'object') return false
  return true
}

/**
 * Строка адреса → объект. Значение пишут двумя способами: base64url — чтобы адрес оставался
 * читаемым и не разъезжался на кавычках, и открытый JSON — чтобы его можно было собрать руками
 * и глазами проверить. Непригодное значение даёт `null`, а НЕ исключение: адрес правят руками и
 * пересылают, и страница, падающая от чужой строки в запросе, хуже страницы без параметра.
 *
 * Общий разбор — чтобы фильтр, группировка и сортировка (`urlView.js`) читались одинаково и не
 * разошлись в том, что считать пригодным.
 */
export function decodeJsonParam(raw) {
  if (!raw || typeof raw !== 'string') return null
  let text = raw.trim()
  if (!text) return null
  try {
    if (!text.startsWith('{')) {
      const b64 = text.replace(/-/g, '+').replace(/_/g, '/')
      text = decodeURIComponent(escape(window.atob(b64)))
    }
    return JSON.parse(text)
  } catch (e) {
    return null
  }
}

/** Строка адреса → фильтр. Непригодное значение даёт `null`, а не исключение. */
export function decodeFilter(raw) {
  const parsed = decodeJsonParam(raw)
  return isFilterShape(parsed) ? parsed : null
}

/**
 * Доращивание «тонкого» условия до полной формы конструктора (BUG-392).
 *
 * ЗАЧЕМ. Из адреса приходит компактное условие: у аргумента-поля в `value` лежит голое число —
 * идентификатор поля. Сервер его читает штатно (`CondArg.readValue` разворачивает и число, и
 * объект), поэтому фильтр ОТБИРАЕТ верно. Но конструктор (`CbsConditionArgument`) держит в
 * `value` ЦЕЛЫЙ объект поля `{title, value:<id>, datatype, entitysid, parententityid…}` и по нему
 * рисует имя поля и выводит `datatype` для аргумента-значения. Число он сопоставить не может —
 * поле показывается пустым. Значит, перед показом число нужно заменить объектом поля, повторив
 * ровно то, что делает `processCondition` после ручного выбора поля.
 *
 * `fields` — список полей объекта в форме `objAttrsFull()` таблицы: `{title, value:<entityId>,
 * datatype, entitysid, parententityid, refobjectid, attributeid}`.
 */
function hydrateCondition(cond, byId) {
  if (!cond || !Array.isArray(cond.args)) return
  const ent = cond.args.find(a => a && a.type && a.type.sid === 'entity')
  const val = cond.args.find(a => a && a.type && a.type.sid === 'value')
  if (!ent) return
  // Объект в `value` — условие уже в полной форме (собрано конструктором или уже доращивалось).
  if (ent.value && typeof ent.value === 'object') return
  const field = byId.get(String(ent.value))
  if (!field) return
  // Дальше — та же раскладка, что в CbsFilterCondition.processCondition: поле держит объект,
  // а его datatype/id уезжают в аргумент-значение, чтобы сервер знал тип сравнения.
  ent.value = { ...field }
  ent.datatype = null
  ent.entityid = null
  ent.entitysid = null
  ent.parententityid = null
  if (val) {
    val.datatype = field.datatype
    val.entityid = field.value
    val.entitysid = field.entitysid
    val.parententityid = field.parententityid
  }
}

function hydrateNode(node, byId) {
  if (!node || typeof node !== 'object') return
  const conds = node.conds || []
  const nodes = node.nodes || []
  conds.forEach(c => hydrateCondition(c, byId))
  nodes.forEach(n => hydrateNode(n, byId))
}

export function hydrateFilter(filter, fields) {
  if (!filter || !filter.node || !Array.isArray(fields)) return filter
  const byId = new Map(fields.map(f => [String(f.value), f]))
  hydrateNode(filter.node, byId)
  return filter
}

/**
 * Подпись условия — чтобы при ОБЪЕДИНЕНИИ фильтров не добавлять одно и то же дважды.
 *
 * Берём то, что делает условие условием: оператор и значения аргументов. У аргумента-поля
 * значение — это идентификатор поля (после доращивания лежит в объекте, до — голым числом),
 * поэтому разворачиваем оба вида. Сложные аргументы (формула, монада) сводятся к JSON — редкий
 * случай, где точное совпадение по строке достаточно.
 */
export function condSignature(cond) {
  try {
    const oper = (cond && cond.oper && cond.oper.sid) || ''
    const args = ((cond && cond.args) || []).map(a => {
      const sid = a && a.type && a.type.sid
      const v = a && a.value
      const raw = v && typeof v === 'object' ? v.value : v
      return `${sid}:${raw}`
    })
    return `${oper}|${args.join('|')}`
  } catch (e) {
    return JSON.stringify(cond)
  }
}

/**
 * Объединение фильтров (BUG-392): условия ДОБАВЛЯЮТСЯ к существующим, а не заменяют их.
 *
 * Дубли отсекаются по подписи — повторный переход по той же ссылке не плодит одинаковые
 * строки. Вложенные узлы добавляются как есть: у них своя структура, и сводить их к подписи
 * значило бы угадывать намерение, которого в ссылке не выражено.
 */
export function mergeFilterInto(node, incoming) {
  // Вызывающий гарантирует, что у узла есть массивы conds/nodes (у userFilter.node так всегда).
  if (!node || !incoming || !Array.isArray(node.conds) || !Array.isArray(node.nodes)) return node
  const seen = new Set(node.conds.map(condSignature))
  const incomingConds = incoming.conds || []
  const incomingNodes = incoming.nodes || []
  incomingConds.forEach(c => {
    const s = condSignature(c)
    if (!seen.has(s)) { node.conds.push(c); seen.add(s) }
  })
  incomingNodes.forEach(n => node.nodes.push(n))
  return node
}

/** Фильтр → строка адреса (base64url). Нужна и тому, кто будет строить ссылку на фронте. */
export function encodeFilter(filter) {
  try {
    const json = JSON.stringify(filter)
    return window.btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  } catch (e) {
    return ''
  }
}
